import angular, {
    ICompileService,
    IDocumentService,
    IScope,
    ITemplateCacheService,
    IWindowService,
} from 'angular';

declare global {
    interface Window {
        opera?: unknown;
    }
}

// this is the input for angular.element
export type ElementLike =
    | string
    | Element
    | Document
    | JQLite
    | ArrayLike<Element>;
export type PopoverAlignment = 'top' | 'bottom' | 'left' | 'right';

export function isPopoverAlignment(x: string): x is PopoverAlignment {
    return ['top', 'bottom', 'left', 'right'].includes(x);
}

function getStyle(o, styleProp) {
    let y;
    if (o.currentStyle) {
        y = o.currentStyle[styleProp];
    } else if (window.getComputedStyle) {
        y = window.getComputedStyle(o, null)[styleProp];
    }
    return y;
}

function isFixed(scr) {
    const result = {
        fixed: false,
        zindex: -1,
    };
    while (scr && scr !== document.body) {
        if (getStyle(scr, 'position') === 'fixed') {
            result.fixed = true;
            result.zindex = getStyle(scr, 'z-index');
            break;
        }
        scr = scr.parentNode;
    }
    return result;
}

function findPos(obj) {
    function scrollDist() {
        const html = document.getElementsByTagName('html')[0];
        if (html.scrollTop && document.documentElement.scrollTop) {
            return [html.scrollLeft, html.scrollTop];
        } else if (html.scrollTop || document.documentElement.scrollTop) {
            return [
                html.scrollLeft + document.documentElement.scrollLeft,
                html.scrollTop + document.documentElement.scrollTop,
            ];
        } else if (document.body.scrollTop) {
            return [document.body.scrollLeft, document.body.scrollTop];
        }
        return [0, 0];
    }

    let curleft = 0,
        curtop = 0,
        scr = obj,
        fixed = false;

    while ((scr = scr.parentNode) && scr !== document.body) {
        curleft -= scr.scrollLeft || 0;
        curtop -= scr.scrollTop || 0;
        if (getStyle(scr, 'position') === 'fixed') {
            fixed = true;
        }
    }
    if (fixed && !window.opera) {
        const scrDist = scrollDist();
        curleft += scrDist[0];
        curtop += scrDist[1];
    }
    do {
        curleft += obj.offsetLeft;
        curtop += obj.offsetTop;
    } while ((obj = obj.offsetParent)); // eslint-disable-line no-cond-assign

    return [curleft, curtop];
}
// http://stackoverflow.com/questions/442404/retrieve-the-position-x-y-of-an-html-element
function getDocumentTopOffset(el) {
    return findPos(el)[1];
}

function getDocumentTopOffsetFixed(el) {
    return (
        el.offsetTop +
        (el.offsetParent ? getDocumentTopOffsetFixed(el.offsetParent) : 0)
    );
}

export interface IPopover {
    // anchor is the element to align the popover to, if not set the popover will align to an anchor provided in the constructor.
    show(anchor?: Element | EventTarget | null): void;
    hide(): void;
    isVisible: boolean;
    firstClick: boolean;
}

// TODO: Split into Popover and PopoverXS classes.
export class PopoverController implements IPopover {
    isVisible = false;
    // this is the popover element itself.
    element: JQLite;
    // static references to click handlers so they can be easily bound and unbound as the popups are shown and hidden.
    clickedOffPopoverHandler = (event: Event): void =>
        this.clickedOffPopover(event);
    onResizeHandler = (): void => this.onResize();
    __originalParent;

    __anchor?: Element; // element which triggered the show()
    __content?: JQuery<Element>; // handle to the #content element
    // this is the element to align the popover to, it is __anchor by default,
    // but can be overridden with .alignment_override
    __alignTo?: Element;
    __alignToRect?: DOMRect;
    __observer: MutationObserver = new MutationObserver(this.onResizeHandler);

    __wasSidebar = false; // flag indicating is the show was triggered from the sidebar.

    firstClick = false;

    scrollTop = 0;
    scrollHeight = 0;
    clientHeight = 0;
    __lastTouchY = 0;

    constructor(
        $window: IWindowService,
        protected $document: IDocumentService,
        element: ElementLike,
        protected watchMutation = true,
    ) {
        this.element = angular.element(element);
        this.__originalParent = angular.element(element).parent();
        const query = document.querySelector('#content');
        if (!query) {
            throw new Error('Popup #content not found');
        }
        // Insert it into the dom if it does not already exist
        this.__content = angular.element(query);
        if (!this.element[0].parentElement) {
            this.__content.append(this.element);
        }

        const scope = this.element.scope();
        if (scope) {
            $window.addEventListener('resize', this.onResizeHandler);
            scope.$on('$destroy', () => {
                $window.removeEventListener('resize', this.onResizeHandler);
            });
        }
    }

    __initializeMutationObserver(): void {
        this.__observer.disconnect();

        // well, c'est la vie I guess
        if (this.watchMutation) {
            // configuration of the observer:
            const config = {
                childList: true,
                characterData: true,
                subtree: true,
            };
            // pass in the target node, as well as the observer options
            this.__observer.observe(this.element[0], config);
        }
    }

    show(anchor?: Element | EventTarget | null): void {
        // hide any already open instances.
        this.hide();
        if (!anchor || !(anchor instanceof Element)) {
            console.error('Popover.show: anchor must be an instance of Element', anchor);
            return;
        }
        this.__anchor = anchor;

        // Sometimes we want to align the popover to a sub-element instead of the button
        const alignOverride = angular
            .element(this.__anchor)
            .find('.alignment_override')
            .toArray()
            .find(
                // this is a visibility test; it'll pick the first visible
                // element of all children with .alignment_override
                (elem) =>
                    !!(
                        elem.offsetWidth ||
                        elem.offsetHeight ||
                        elem.getClientRects().length
                    )
            );
        this.__alignTo = alignOverride ? alignOverride : this.__anchor;
        this.__alignToRect = this.__alignTo.getBoundingClientRect();
        this.__wasSidebar =
            angular.element(this.__anchor).parents('#sidebar').length > 0;
        const { fixed } = isFixed(this.__anchor);

        const parentNode = this.element[0].parentNode;
        if (!parentNode || (parentNode instanceof HTMLElement && parentNode.tagName !== 'BODY')) {
            angular.element('body').append(this.element);
        }

        this.element.addClass('is_active');
        if (fixed) {
            this.element.addClass('popover_fixed');

            // Search for scrollable elements and hookup popover overscroll prevention
            const scrollableElement = this.element.find('.popover_scrollable');
            if (scrollableElement.length) {
                scrollableElement.on(
                    'wheel mousewheel touchmove touchstart',
                    (event) => this.handlePopoverScroll(event)
                );
            }
        } else {
            this.element.removeClass('popover_fixed');
        }
        // console.log(i++, this.element);

        this.alignPopover();

        this.isVisible = true;
        this.firstClick = true;

        this.$document.bind('click', this.clickedOffPopoverHandler);

        if (!fixed && !this.__wasSidebar) {
            this._scrollIntoView();
        }

        if (this.__wasSidebar) {
            this.element.addClass('popover_sidebar');
            angular.element(document).on('scroll', this.onResizeHandler);
        }

        setTimeout(() => this.__initializeMutationObserver(), 10);
    }

    /**
     * What exactly is this meant to do? Are we trying to prevent scrolling from exiting the popover if started in the popover?
     * @param event
     * @returns
     */
    handlePopoverScroll(event: JQuery.TriggeredEvent): false | undefined {
        const originalEvent = event.originalEvent;
        if (!originalEvent) {
            return;
        }
        let deltaY = 0;
        let clientY = 0;

        if (event instanceof WheelEvent) {
            deltaY = event.type == 'mousewheel' ? -event.deltaY : event.deltaY;
        }

        if (event instanceof TouchEvent) {
            if (event.type == 'touchstart') {
                this.__lastTouchY = event.touches[0].clientY || 0;
                return;
            }
            if (event.type == 'touchmove') {
                clientY = event.touches[0].clientY || 0;
                deltaY = this.__lastTouchY - clientY;
                this.__lastTouchY = clientY;
            }
        }

        const isTop = this.scrollTop === 0;
        const isBottom =
            this.clientHeight + this.scrollTop === this.scrollHeight;

        if (event.cancelable && (isTop || isBottom)) {
            if (!deltaY || (isTop && deltaY < 0) || (isBottom && deltaY > 0)) {
                return false;
            }
        }
    }

    _scrollIntoView(): void {
        if (!this.__anchor) {
            return;
        }
        const buttonY = getDocumentTopOffset(angular.element(this.__anchor)[0]);

        const $body = angular.element('body');
        const stopExternalScroll = () => $body.stop();
        angular
            .element('body')
            //  prevent external events from scrolling the body while animating our own scroll to top.
            .on(
                'scroll mousedown wheel DOMMouseScroll mousewheel keyup touchmove',
                stopExternalScroll
            )
            .animate(
                { scrollTop: buttonY - window.innerHeight * 0.2 },
                'slow',
                () => {
                    // when done, renable external scrolling
                    $body.off(
                        'scroll mousedown wheel DOMMouseScroll mousewheel keyup touchmove',
                        stopExternalScroll
                    );
                }
            );
    }

    alignPopover(): void {
        const arrow = angular.element(this.element.children()[0]);
        if (!this.__alignTo) {
            this.hide();
            return;
        }
        this.__alignToRect = this.__alignTo.getBoundingClientRect();
        const anchor = this.__anchor;
        if (!anchor) {
            // if there is no anchor, hide rather than align. this generally shouldn't happen.
            this.hide();
            return;
        }

        const anchorRect = anchor.getBoundingClientRect();
        const elRect = this.element[0].getBoundingClientRect();
        const arrowRect = arrow[0].getBoundingClientRect();
        const { fixed } = isFixed(this.__anchor);
        let forceTop = this.element.hasClass('popover_top_force');
        const anchorY = fixed
            ? getDocumentTopOffsetFixed(angular.element(anchor)[0])
            : getDocumentTopOffset(angular.element(anchor)[0]);

        let sidebarScroll = 0;
        if (this.__wasSidebar) {
            const sidebar = document.querySelectorAll(
                'div[ui-view="sidebar"]'
            )[0];
            sidebarScroll = sidebar ? -sidebar.scrollTop : 0;
        }

        // Force top if element is fixed and cut off from the bottom
        if (fixed && !forceTop) {
            const position =
                anchorY + anchorRect.height + sidebarScroll + arrowRect.height;
            forceTop = position + elRect.height > window.innerHeight;
        }

        // Add two extra pixels to account for positioning discrepancy
        const arrowWidthOffset = (arrowRect.width + 2) / 2;

        // Reset alignment in case of screen resize
        this.element
            .removeClass('popover_left')
            .removeClass('popover_right')
            .removeClass('popover_top')
            .removeAttr('style');
        arrow.removeAttr('style');

        if (forceTop) {
            this.element.addClass('popover_top');
            const elHeight = this.element[0].getBoundingClientRect().height;
            this.element.css({
                top: anchorY - elHeight + sidebarScroll - arrowRect.height - 5,
            });
            // Align the arrow to the bottom of the element
            arrow.css({
                top: this.element[0].clientHeight,
            });
        } else {
            this.element.addClass('popover_bottom');
            this.element.css({
                top: anchorY + anchorRect.height + sidebarScroll,
            });
        }

        // Position popover horizontally
        this._positionHorizontally();

        // Align the arrow to the center of the element or sub element
        const left =
            this.__alignToRect.left -
            this.element[0].getBoundingClientRect().left +
            this.__alignToRect.width / 2 -
            arrowWidthOffset;
        arrow.css({
            left,
        });
    }

    _positionHorizontally(): void {
        if (!this.__alignToRect) {
            return;
        }
        const popoverWidthOffset =
            this.element[0].getBoundingClientRect().width / 2;
        const mainContent = angular.element('body');
        // Position popover horizontally
        if (
            this.__alignToRect.left - popoverWidthOffset <
            parseInt(mainContent.css('padding-left'))
        ) {
            // Align popover to the left of the main content box
            this.element.addClass('popover_left');
            this.element.css({
                left: mainContent.css('padding-left'),
            });
        } else if (
            this.__alignToRect.left +
                this.__alignToRect.width / 2 +
                popoverWidthOffset >
            mainContent[0].getBoundingClientRect().right
        ) {
            // Align popover to the right of the main content box
            this.element.addClass('popover_right');
        } else {
            // TODO: switch to boostrap style dropdowns and
            // popovers, instead of doing what ever we're doing
            // here.

            // Align popover to the center of the button
            // If it's a child of #content we need to offset by #content's left margin
            const contentOffset = this.element.parents('#content').length
                ? parseInt(this.__content?.css('margin-left') || '0')
                : 0;

            const left =
                this.__alignToRect.left -
                popoverWidthOffset +
                this.__alignToRect.width / 2 -
                contentOffset;
            this.element.css({ left });
        }
    }

    hide(): void {
        if (!this.isVisible) return;
        this.__observer.disconnect();
        this.__originalParent.append(this.element);
        this.element.removeClass('is_active');
        this.$document.unbind('click', this.clickedOffPopoverHandler);
        angular.element(document).off('scroll', this.onResizeHandler);
        this.isVisible = false;
    }

    $onDestroy(): void {
        this.__observer.disconnect();
        this.$document.unbind('click', this.clickedOffPopoverHandler);
        angular.element(document).off('scroll', this.onResizeHandler);
    }

    clickedOffPopover(event: Event): void {
        const target = angular.element(event.target as HTMLElement);
        const parentIsTypeaheadChoice =
            target.parents('li[id*="typeahead"]').length > 0;
        const isInside =
            this.element.has(event.target as HTMLElement).length > 0 ||
            this.element[0] === event.target ||
            parentIsTypeaheadChoice || //Workaround for #434
            target.closest('.prevent_close').length > 0;
        if (!isInside) {
            // We want the trigger button to close the popover -- but we need to allow
            // the first hit else it'll close as soon as it opens and therefore won't be visible
            if (this.firstClick) {
                this.firstClick = false;
            }
            else {
                this.hide();
            }
        }
    }

    onResize(): void {
        if (this.isVisible) {
            const boundingRect = this.__anchor?.getBoundingClientRect();
            //If popover is visible, and the button is still visible then
            //realign it.  Otherwise hide the popover if it is not already hidden.
            if (boundingRect?.width && this.__anchor) {
                this.alignPopover();
            } else {
                this.hide();
            }
        }
    }
}

// implements the popover "interface" for returning when an invalid selector
// was given to popoverXS
class NullPopover implements IPopover {
    show() {
        return;
    }
    hide() {
        return;
    }
    isVisible = false;
    firstClick = false;
}

export class PopoverService {
    // keep track of popovers so they can be hid.
    popovers: PopoverController[] = [];
    static $inject = ['$compile', '$templateCache', '$document', '$window'];
    constructor(
        protected $compile: ICompileService,
        protected $templateCache: ITemplateCacheService,
        protected $document: IDocumentService,
        protected $window: IWindowService
    ) {}

    fromTemplate(
        templateName: string,
        scope: IScope,
        watchMutation = true
    ): IPopover {
        // TODO: Consolidate all popovers to one type.
        // console.log('$tnPopover.fromTemplate', templateName, scope, watchMutation);
        const _templateStr =
            this.$templateCache.get<string>(templateName) || '';
        const templateFunc = this.$compile(_templateStr);
        const template = templateFunc(scope);
        const popover = new PopoverController(
            this.$window,
            this.$document,
            template,
            watchMutation
        );
        this.popovers.push(popover);
        return popover;
    }

    popoverXS(element: ElementLike, watchMutation = true): IPopover {
        // TODO: consolidate all popovers to one type.
        // console.log('$tnPopover.popoverXS', element, watchMutation);
        const $el = angular.element(element);
        if ($el.length < 1) {
            console.error(
                'empty popover result for ',
                element,
                '; returning nil'
            );
            return new NullPopover();
        }
        if ($el.length > 1) {
            console.error(
                'too many popover results for ',
                element,
                '; returning nil'
            );
            return new NullPopover();
        }
        const popover = new PopoverController(
            this.$window,
            this.$document,
            element,
            watchMutation
        );
        this.popovers.push(popover);
        return popover;
    }

    /**
     * hide all managed popovers.
     */
    hide(): void {
        this.popovers.forEach((popover) => {
            popover.hide();
        });
    }
}
